import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Secao from '../../components/ui/Secao';
import { Input } from '../../components/ui/Input';
import { propostasApi } from '../../api/vendas';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function itemPendente(item) {
  return item.margem_promocao_valor != null
    && Number(item.valor_unitario) < Number(item.margem_promocao_valor)
    && Number(item.valor_unitario) !== Number(item.valor_aprovado_gerente);
}

export default function NegociacaoDetail({ propostaId, onVoltar }) {
  useTituloPagina('Negociação de Preço');
  const [proposta, setProposta] = useState(null);
  const [decisoes, setDecisoes] = useState({});
  const [observacao, setObservacao] = useState('');
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    propostasApi.obter(propostaId).then(p => {
      setProposta(p);
      const inicial = {};
      p.itens.filter(itemPendente).forEach(item => { inicial[item.id] = { valor: Number(item.valor_unitario).toFixed(2), aprovado: null }; });
      setDecisoes(inicial);
    }).catch(() => {});
  }, [propostaId]);

  if (!proposta) return null;

  const pendentes = proposta.itens.filter(itemPendente);
  const outros = proposta.itens.filter(i => !itemPendente(i));
  const faltamDecisoes = pendentes.some(i => decisoes[i.id]?.aprovado === null);

  const definirValor = (id, valor) => setDecisoes(d => ({ ...d, [id]: { ...d[id], valor } }));
  const decidir = (id, aprovado) => setDecisoes(d => ({ ...d, [id]: { ...d[id], aprovado } }));

  const concluir = async () => {
    setProcessando(true);
    setErro('');
    try {
      await propostasApi.resolverNegociacao(propostaId, {
        observacao,
        itens: pendentes.map(i => ({ id: i.id, valor_unitario: decisoes[i.id].valor, aprovado: decisoes[i.id].aprovado }))
      });
      onVoltar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 14 }}>
          <div>
            <strong style={{ fontSize: 15 }}>Proposta #{proposta.numero} — {proposta.cliente_nome}</strong>
            <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>Vendedor: {proposta.vendedor_nome}</div>
          </div>
          <Btn variante="ghost" onClick={onVoltar}>Voltar</Btn>
        </div>

        {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}

        <Secao titulo="Itens Abaixo da Tabela Promoção">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pendentes.map(item => {
              const decisao = decisoes[item.id] || { valor: Number(item.valor_unitario).toFixed(2), aprovado: null };
              return (
                <div key={item.id} style={{ background: COLORS.warningBg, border: `1px solid ${COLORS.warning}66`, borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>{item.produto_codigo}</strong> — {item.produto_nome}
                      <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>
                        Quantidade: {Number(item.quantidade).toLocaleString('pt-BR')} · Vendedor pediu: {formatarReais(item.valor_unitario)} · Tabela Promoção: {formatarReais(item.margem_promocao_valor)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Input type="number" step="0.01" value={decisao.valor} style={{ width: 100 }}
                        onChange={e => definirValor(item.id, e.target.value)} />
                      <Btn tamanho="sm" variante={decisao.aprovado === true ? 'primary' : 'secondary'} onClick={() => decidir(item.id, true)}>Aprovar</Btn>
                      <Btn tamanho="sm" variante={decisao.aprovado === false ? 'danger' : 'secondary'} onClick={() => decidir(item.id, false)}>Negar</Btn>
                    </div>
                  </div>
                </div>
              );
            })}
            {pendentes.length === 0 && (
              <p style={{ fontSize: 13, color: COLORS.textMid }}>Nenhum item pendente de decisão nesta proposta.</p>
            )}
          </div>
        </Secao>

        {outros.length > 0 && (
          <>
            <div style={{ height: 14 }} />
            <Secao titulo="Demais Itens da Proposta">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', background: COLORS.bg }}>
                    <th style={{ padding: 8 }}>Produto</th>
                    <th style={{ padding: 8 }}>Quantidade</th>
                    <th style={{ padding: 8 }}>Valor Unit.</th>
                  </tr>
                </thead>
                <tbody>
                  {outros.map(item => (
                    <tr key={item.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: 8 }}>{item.produto_codigo} - {item.produto_nome}</td>
                      <td style={{ padding: 8 }}>{Number(item.quantidade).toLocaleString('pt-BR')}</td>
                      <td style={{ padding: 8 }}>{formatarReais(item.valor_unitario)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Secao>
          </>
        )}

        <div style={{ height: 14 }} />

        <Secao titulo="Observação da Negociação (a vendedora vai ver ao reabrir a proposta)">
          <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={4}
            style={{ width: '100%', border: `1.5px solid ${COLORS.border}`, borderRadius: 7, padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        </Secao>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Btn onClick={concluir} disabled={processando || faltamDecisoes} title={faltamDecisoes ? 'Aprove ou negue todos os itens pendentes' : ''}>
            {processando ? 'Enviando...' : 'Concluir Negociação e Devolver à Vendedora'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}
